<script setup lang="ts">
import ProgressFrame from './layout/ProgressFrame.vue';
import { useRouter } from 'vue-router';
import ConfigureInverterSystemSpec from './ConfigureInverterSystemSpec.vue';
import ConfigureCTSpec from './ConfigureCTSpec.vue';
import { reactive, ref } from 'vue';
import type { SolshareSystemSpec, InverterSpec, CTSpec } from '../models/SolshareSystemSpec';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { isCTSpecStepRequired } from '../constants/config-constants';

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const router = useRouter();

type InverterAndCTSpec = Pick<SolshareSystemSpec, 'inverterSpec' | 'ctSpec'>;
type StepName = keyof InverterAndCTSpec;

const ctSpecStepRequired = isCTSpecStepRequired(serialNumber);

const subStepsDetails: Record<
  StepName,
  {
    title: string;
    subTitle: string;
    backAction: (() => void) | undefined;
    nextStep: keyof InverterAndCTSpec | null;
    submitNow: boolean;
  }
> = {
  inverterSpec: {
    title: 'System Specifications',
    subTitle: serialNumber,
    backAction: undefined,
    nextStep: ctSpecStepRequired ? 'ctSpec' : null,
    submitNow: !ctSpecStepRequired,
  },
  ctSpec: {
    title: 'Which CTs were used for this SolShare?',
    subTitle: serialNumber,
    backAction: () => {
      currentStep.value = 'inverterSpec';
    },
    nextStep: null,
    submitNow: true,
  },
};

const currentStep = ref<StepName>('inverterSpec');

const solshareSpecs = reactive<InverterAndCTSpec>({
  inverterSpec: {
    systemSize: undefined,
    capacity: undefined,
    brand: undefined,
    otherBrand: undefined,
  },
  ctSpec: {
    rating: undefined,
  },
});

const handleNext = <T extends StepName>(specName: T, data: InverterAndCTSpec[T]) => {
  const submitNow = subStepsDetails[currentStep.value].submitNow;
  solshareSpecs[specName] = data;

  if (submitNow) {
    router.push({ name: 'ConfigureSolshareReviewLiveConnections' });
  } else {
    currentStep.value = subStepsDetails[specName].nextStep as StepName;
  }
};
</script>

<template>
  <ProgressFrame
    pageClass="subsection-page"
    withBackArrow
    :customBackAction="subStepsDetails[currentStep].backAction"
    titleTight
    subTitleColor="secondary"
  >
    <template #title>{{ subStepsDetails[currentStep].title }}</template>
    <template #subTitle> {{ subStepsDetails[currentStep].subTitle }} </template>
    <div class="content">
      <ConfigureInverterSystemSpec
        v-if="currentStep === 'inverterSpec'"
        :serialNumber="serialNumber"
        @next="(data: InverterSpec) => handleNext('inverterSpec', data)"
      />
      <ConfigureCTSpec
        v-if="currentStep === 'ctSpec'"
        :serialNumber="serialNumber"
        @next="(data: CTSpec) => handleNext('ctSpec', data)"
      />
    </div>
  </ProgressFrame>
</template>
